export type JobAdmissionCode =
  | "job_rate_limited"
  | "job_backlog_limit_exceeded"
  | "job_queue_depth_limit_exceeded"
  | "job_payload_too_large"
  | "job_min_delay_violation"
  | "job_schedule_horizon_exceeded"
  | "job_admission_disabled";

export interface JobAdmissionErrorOptions {
  status?: number;
  retryAfterMs?: number;
  details?: Record<string, unknown>;
}

export interface JobAdmissionErrorBody {
  error: string;
  code: JobAdmissionCode;
  retryAfterMs?: number;
  details?: Record<string, unknown>;
}

export class JobAdmissionError extends Error {
  readonly code: JobAdmissionCode;
  readonly status: number;
  readonly retryAfterMs?: number;
  readonly details?: Record<string, unknown>;

  constructor(code: JobAdmissionCode, message: string, options: JobAdmissionErrorOptions = {}) {
    super(message);
    this.name = "JobAdmissionError";
    this.code = code;
    this.status = options.status ?? getJobAdmissionStatus(code);
    this.retryAfterMs = options.retryAfterMs;
    this.details = options.details;
  }
}

export function getJobAdmissionStatus(code: JobAdmissionCode): number {
  switch (code) {
    case "job_payload_too_large":
      return 413;
    case "job_min_delay_violation":
    case "job_schedule_horizon_exceeded":
      return 400;
    case "job_admission_disabled":
      return 503;
    case "job_rate_limited":
    case "job_backlog_limit_exceeded":
    case "job_queue_depth_limit_exceeded":
      return 429;
  }
}

export function isJobAdmissionError(error: unknown): error is JobAdmissionError {
  return error instanceof JobAdmissionError;
}

export function buildJobAdmissionErrorBody(error: JobAdmissionError): JobAdmissionErrorBody {
  return {
    error: error.message,
    code: error.code,
    ...(error.retryAfterMs != null ? { retryAfterMs: error.retryAfterMs } : {}),
    ...(error.details ? { details: error.details } : {}),
  };
}

export function jsonByteLength(value: unknown): number {
  try {
    return new TextEncoder().encode(JSON.stringify(value)).length;
  } catch {
    return Number.POSITIVE_INFINITY;
  }
}

export function readPositiveIntegerEnv(
  env: Record<string, string | undefined>,
  key: string,
): number | undefined {
  const raw = env[key];
  if (raw == null || raw.trim() === "") return undefined;
  const value = Number(raw);
  if (!Number.isInteger(value) || value <= 0) return undefined;
  return value;
}

export function readNonNegativeIntegerEnv(
  env: Record<string, string | undefined>,
  key: string,
): number | undefined {
  const raw = env[key];
  if (raw == null || raw.trim() === "") return undefined;
  const value = Number(raw);
  if (!Number.isInteger(value) || value < 0) return undefined;
  return value;
}

export function readBooleanEnv(env: Record<string, string | undefined>, key: string): boolean {
  const raw = env[key]?.trim().toLowerCase();
  return raw === "1" || raw === "true" || raw === "yes" || raw === "on";
}

export function getMinuteBucketStart(date = new Date()): Date {
  const bucket = new Date(date);
  bucket.setUTCSeconds(0, 0);
  return bucket;
}

export function getMsUntilNextMinute(date = new Date()): number {
  const next = getMinuteBucketStart(date);
  next.setUTCMinutes(next.getUTCMinutes() + 1);
  return Math.max(1_000, next.getTime() - date.getTime());
}

export function isJobAdmissionKillSwitchActive(
  env: Record<string, string | undefined>,
  scope: string,
): boolean {
  const raw =
    env.GENCOW_JOB_ADMISSION_KILL_SWITCH ?? env.GENCOW_JOB_KILL_SWITCH ?? env.GENCOW_PLATFORM_JOB_KILL_SWITCH;
  if (!raw) return false;

  const normalized = raw
    .split(",")
    .map((item) => item.trim().toLowerCase())
    .filter(Boolean);
  if (normalized.length === 0) return false;
  if (normalized.some((item) => item === "1" || item === "true" || item === "all")) return true;

  const wanted = scope.toLowerCase();
  return normalized.some((item) => wanted === item || wanted.startsWith(`${item}:`));
}
